import { useState } from 'react';
import { ChevronDown, LogOut, Menu, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { LanguageSwitcher } from '@/shared/ui/feedback/LanguageSwitcher';
import type { ApiAuthMeStaffRole } from '@/shared/types/api/auth';

type Props = {
  fullName?: string;
  username?: string;
  meRole: ApiAuthMeStaffRole | null;
  onToggleSidebar?: () => void;
  onLogout: () => void;
};

export const Topbar = ({
  fullName,
  username,
  meRole,
  onToggleSidebar,
  onLogout,
}: Props) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Trigger: full name (trimmed) → username → generic i18n fallback.
  const triggerLabel = fullName?.trim() || username || t('header.user');
  // Dropdown / sidebar role subtitle: super staff get a translated label;
  // custom roles render their literal `role.name` (NOT through i18n, because
  // owners enter free-text names that have no translation key).
  let roleSubtitle: string | null = null;
  if (meRole?.is_super === true) {
    roleSubtitle = t('header.superLabel');
  } else if (meRole?.name) {
    roleSubtitle = meRole.name;
  }
  return (
    <div className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-6">
      <div className="flex items-center gap-2">
        {onToggleSidebar && (
          <IconButton label={t('ui.menu')} variant="ghost" onClick={onToggleSidebar}>
            <Menu className="h-5 w-5" />
          </IconButton>
        )}
      </div>
      <div className="relative flex items-center gap-3">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className="inline-flex max-w-[260px] cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-left shadow-sm transition-colors hover:bg-slate-50 active:bg-slate-100"
        >
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700 ring-2 ring-brand-50">
            <User className="h-4 w-4" aria-hidden />
          </span>
          <span className="hidden max-w-[160px] truncate text-sm font-medium text-ink sm:inline">
            {triggerLabel}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-ink-muted transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
        {menuOpen && <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />}
        <div
          aria-hidden={!menuOpen}
          className={`absolute right-0 top-[calc(100%+0.5rem)] z-50 w-56 rounded-xl border border-slate-200 bg-white p-1 shadow-popover transition-all duration-150 ease-out ${
            menuOpen
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none -translate-y-1 opacity-0'
          }`}
        >
          <div className="px-3 py-2.5">
            {username && (
              <p className="truncate text-sm font-medium text-ink">{username}</p>
            )}
            {roleSubtitle && (
              <p className="mt-0.5 truncate text-xs text-ink-muted">{roleSubtitle}</p>
            )}
          </div>
          <div className="my-1 h-px bg-slate-100" />
          <div className="px-3 py-2">
            <p className="mb-1.5 text-xs font-medium text-ink-muted">{t('common.language')}</p>
            <LanguageSwitcher variant="pills" className="w-full justify-between" />
          </div>
          <div className="my-1 h-px bg-slate-100" />
          <button
            type="button"
            onClick={() => {
              setMenuOpen(false);
              onLogout();
            }}
            className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            {t('topbar.menu.logout')}
          </button>
        </div>
      </div>
    </div>
  );
};
